/**
 * Creation panel shown on the Overview tab while a site has no live version:
 * a title row plus three equal glass option cards (file drop, AI agent,
 * Figma link).
 *
 * The whole panel is one react-dropzone root (noClick), so dragging a folder
 * or .zip anywhere over it publishes exactly like today's dropzone did; the
 * first card's click opens the file browser via dropzone's open(). Deploy
 * state stays in AeroSiteDetail so an in-flight publish survives tab
 * switches, same as before.
 */
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { getFilesFromEvent } from '../../utils/htmlDeploy/gatherFiles';
import AgentPublishModal from '../../components/shared/AgentPublishModal';
import AeroIcon from '../../components/Aero/AeroIcon';
import FigmaLinkModal from './FigmaLinkModal';

function SiteCreatePanel({ user, ownerUid, site, deploying, progress, onDrop, onEmbedded }) {
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showFigmaModal, setShowFigmaModal] = useState(false);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    getFilesFromEvent,
    disabled: deploying || !site,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  const progressLabel = () => {
    if (!progress) return 'Publishing…';
    if (progress.phase === 'hashing') {
      return `Hashing ${progress.hashed}/${progress.total}…`;
    }
    if (progress.phase === 'uploading') {
      const pct = progress.totalUploadBytes
        ? Math.round((progress.uploadedBytes / progress.totalUploadBytes) * 100)
        : 100;
      return `Uploading ${progress.blobsUploaded}/${progress.blobsTotal} files (${pct}%)…`;
    }
    return 'Publishing…';
  };

  return (
    <div
      {...getRootProps({
        className: `aero-sites-create-panel aero-w-publish${isDragActive ? ' active' : ''}`,
      })}
    >
      <input {...getInputProps({ webkitdirectory: 'true', directory: 'true' })} />

      <h2 className="aero-sites-create-title">{site.title || site.generatedUrl} isn't live yet</h2>
      <p className="aero-page-sub aero-sites-create-sub">
        Choose how to publish it. Any of the three ways works.
      </p>

      <div className="aero-sites-create-options">
        {/* 1. Drop files */}
        <button
          type="button"
          className="aero-card aero-card-pad aero-sites-option-card"
          onClick={open}
          disabled={deploying}
        >
          <div className="aero-sites-option-icon">
            <AeroIcon name="up" />
          </div>
          <h3>Drop your site files</h3>
          <p>
            {deploying
              ? progressLabel()
              : isDragActive
                ? 'Drop the folder to publish'
                : 'Drag a folder or .zip of your built site anywhere on this page, or browse. Live in seconds.'}
          </p>
        </button>

        {/* 2. AI agent */}
        <button
          type="button"
          className="aero-card aero-card-pad aero-sites-option-card"
          onClick={() => setShowAgentModal(true)}
        >
          <div className="aero-sites-option-icon">
            <AeroIcon name="prompt" />
          </div>
          <h3>
            From your AI agent <span className="aero-smp">New</span>
          </h3>
          <p>
            Claude Code, Cursor or Cowork can build and publish this site with
            one prompt.
          </p>
        </button>

        {/* 3. Figma link */}
        <button
          type="button"
          className="aero-card aero-card-pad aero-sites-option-card"
          onClick={() => setShowFigmaModal(true)}
        >
          <div className="aero-sites-option-icon">
            <AeroIcon name="figma" />
          </div>
          <h3>From a Figma link</h3>
          <p>Embed your prototype or convert it to code.</p>
        </button>
      </div>

      {showAgentModal && (
        <AgentPublishModal
          user={user}
          site={site}
          onClose={() => setShowAgentModal(false)}
        />
      )}
      {showFigmaModal && (
        <FigmaLinkModal
          user={user}
          ownerUid={ownerUid}
          site={site}
          onClose={() => setShowFigmaModal(false)}
          onEmbedded={onEmbedded}
          onSwitchToAgent={() => setShowAgentModal(true)}
        />
      )}
    </div>
  );
}

export default SiteCreatePanel;
